import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '@auth0/auth0-angular';
import { switchMap } from 'rxjs/operators';

export interface SessionExpiredDialogData {
  email?: string;
  sub?: string;
}

/**
 * Erneute Anmeldung bei abgelaufener Sitzung (DOCU-1323). Per Popup statt
 * Redirect: die Seite bleibt stehen, ungespeicherte Eingaben bleiben erhalten,
 * und der zurueckgehaltene Request wird danach mit dem neuen Token gesendet.
 * Schliesst mit dem Access-Token oder — bei „Später“ — ohne Wert.
 */
@Component({
  selector: 'app-session-expired-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, TranslateModule],
  templateUrl: './session-expired-dialog.component.html',
})
export class SessionExpiredDialogComponent {
  busy = false;
  failed = false;

  constructor(
    public dialogRef: MatDialogRef<SessionExpiredDialogComponent, string>,
    @Inject(MAT_DIALOG_DATA) public data: SessionExpiredDialogData,
    private auth: AuthService
  ) {}

  login() {
    this.busy = true;
    this.failed = false;

    // loginWithPopup muss synchron im Klick-Handler starten, sonst blockt der
    // Browser das Popup. Standard-Timeout des SDK sind 60 s — zu knapp, wenn
    // jemand erst sein Passwort heraussucht; danach bricht es mit `timeout` ab.
    this.auth
      .loginWithPopup(
        { authorizationParams: { login_hint: this.data.email } },
        { timeoutInSeconds: 300 }
      )
      .pipe(switchMap(() => this.auth.getAccessTokenSilently()))
      .subscribe({
        next: token => {
          // Anderes Konto angemeldet: die zurueckgehaltenen Requests duerfen
          // nicht unter fremdem Namen gespeichert werden.
          if (this.data.sub && tokenSubject(token) !== this.data.sub) {
            this.dialogRef.close();
            this.auth.logout({ logoutParams: { returnTo: window.location.origin } });
            return;
          }
          this.dialogRef.close(token);
        },
        error: err => {
          console.error('Re-login failed:', err);
          this.busy = false;
          if (err?.error === 'cancelled' || err?.error === 'popup_closed_by_user') {
            return;
          }
          this.failed = true;
        },
      });
  }
}

function tokenSubject(token: string): string | undefined {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(payload)).sub;
  } catch {
    return undefined;
  }
}
